import { API_BASE_URL, enforceAuthResponse, getAuthHeaders } from "../client";

export type WaiverStatus = "pending" | "approved" | "rejected" | "all";

export interface ChargesWaiverItem {
  transaction_id: number;
  waiver_ref: string;
  loan_id: number;
  applicant_id?: string | null;
  applicant_name?: string | null;
  amount: number;
  charges_due: number;
  approval_status_id?: number | null;
  approval_status: string;
  transaction_status?: string | null;
  reason?: string | null;
  requested_by?: number | null;
  requested_by_name?: string | null;
  requested_by_role?: string | null;
  requested_at?: string | null;
  decided_by?: number | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  decision_reason?: string | null;
  rm_name?: string | null;
  tl_name?: string | null;
  branch?: string | null;
  lender?: string | null;
}

export interface ChargesWaiverListResponse {
  status: string;
  counts: { pending: number; approved: number; rejected: number };
  total: number;
  results: ChargesWaiverItem[];
}

export interface ChargesWaiverHistoryResponse {
  loan_id: number;
  available_for_waiver: number;
  results: ChargesWaiverItem[];
}

export interface ChargesWaiverCreateResponse {
  transaction_id: number;
  loan_id: number;
  amount: number;
  approval_status: string;
  available_before: number;
  available_after: number;
  message: string;
}

export interface WaiverDecisionResponse {
  action: string;
  total: number;
  succeeded: number;
  failed: number;
  message: string;
  results: Array<{
    transaction_id: number;
    success: boolean;
    error?: string | null;
    message?: string | null;
  }>;
}

async function readError(response: Response): Promise<string> {
  enforceAuthResponse(response);
  const data = await response.json().catch(() => ({}));
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item: { msg?: string }) => item?.msg || "Invalid request").join("; ");
  }
  return `Request failed: ${response.status}`;
}

export class ChargesWaiverService {
  static async create(
    loanId: number,
    payload: { amount: number; reason: string }
  ): Promise<ChargesWaiverCreateResponse> {
    const response = await fetch(`${API_BASE_URL}/charges-waiver/loan/${loanId}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  static async history(loanId: number): Promise<ChargesWaiverHistoryResponse> {
    const response = await fetch(`${API_BASE_URL}/charges-waiver/loan/${loanId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  static async list(params: {
    status?: WaiverStatus;
    search?: string;
    sort?: "requested_desc" | "requested_asc";
    limit?: number;
    offset?: number;
  } = {}): Promise<ChargesWaiverListResponse> {
    const query = new URLSearchParams({
      status: params.status || "pending",
      sort: params.sort || "requested_desc",
    });
    if (params.search) query.set("search", params.search);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.offset != null) query.set("offset", String(params.offset));
    const response = await fetch(`${API_BASE_URL}/charges-waiver/?${query}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  static async decide(payload: {
    transaction_id?: number;
    transaction_ids?: number[];
    action: "accept" | "reject";
    comments?: string;
  }): Promise<WaiverDecisionResponse> {
    const response = await fetch(`${API_BASE_URL}/charges-waiver/decision`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }
}
